import React, { useEffect } from 'react';
import { MapPinIcon, PhoneIcon, GlobeIcon, UsersIcon, BuildingIcon, CalendarIcon, HeartIcon, ShieldIcon, BriefcaseIcon, ExternalLinkIcon, XIcon, ClipboardListIcon, HistoryIcon, InfoIcon } from 'lucide-react';
type PayerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  payer: any; // In a real app, we would define a proper type for payer
};
export function PayerModal({
  isOpen,
  onClose,
  payer
}: PayerModalProps) {
  // Close modal when Escape key is pressed
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  if (!payer) return null;
  // Generate mock data based on payer name
  const getPayerDetails = (payerName: string) => {
    const payerMap: Record<string, any> = {
      'Blue Cross and Blue Shield of New Mexico': {
        website: 'www.bcbsnm.com',
        address: '5701 Balloon Fiesta Parkway NE, Albuquerque, NM 87113',
        phone: '123-456-789',
        region: 'National',
        membership: 'Global',
        insuredLives: '23.7 M',
        medicare: 'Yes',
        medicaid: 'Yes',
        commercial: 'Yes',
        logo: 'https://logo.clearbit.com/bcbs.com',
        type: 'Insurance'
      },
      UnitedHealthcare: {
        website: 'www.uhc.com',
        address: '9900 Bren Road East, Minnetonka, MN 55343',
        phone: '800-328-5979',
        region: 'National',
        membership: 'Global',
        insuredLives: '49.5 M',
        medicare: 'Yes',
        medicaid: 'Yes',
        commercial: 'Yes',
        logo: 'https://logo.clearbit.com/uhc.com',
        type: 'Insurance'
      },
      Aetna: {
        website: 'www.aetna.com',
        address: '151 Farmington Ave, Hartford, CT 06156',
        phone: '800-872-3862',
        region: 'National',
        membership: 'Global',
        insuredLives: '39.1 M',
        medicare: 'Yes',
        medicaid: 'Yes',
        commercial: 'Yes',
        logo: 'https://logo.clearbit.com/aetna.com',
        type: 'Insurance'
      },
      Cigna: {
        website: 'www.cigna.com',
        address: '900 Cottage Grove Rd, Bloomfield, CT 06002',
        phone: '800-997-1654',
        region: 'National',
        membership: 'Global',
        insuredLives: '17.8 M',
        medicare: 'Yes',
        medicaid: 'Yes',
        commercial: 'Yes',
        logo: 'https://logo.clearbit.com/cigna.com',
        type: 'Insurance'
      },
      Humana: {
        website: 'www.humana.com',
        address: '500 W Main St, Louisville, KY 40202',
        phone: '800-457-4708',
        region: 'National',
        membership: 'Global',
        insuredLives: '16.9 M',
        medicare: 'Yes',
        medicaid: 'Yes',
        commercial: 'Yes',
        logo: 'https://logo.clearbit.com/humana.com',
        type: 'Insurance'
      },
      'Anthem Blue Cross': {
        website: 'www.anthem.com',
        address: '220 Virginia Ave, Indianapolis, IN 46204',
        phone: '800-331-1476',
        region: 'National',
        membership: 'Global',
        insuredLives: '45.2 M',
        medicare: 'Yes',
        medicaid: 'Yes',
        commercial: 'Yes',
        logo: 'https://logo.clearbit.com/anthem.com',
        type: 'Insurance'
      },
      'Kaiser Permanente': {
        website: 'www.kp.org',
        address: 'One Kaiser Plaza, Oakland, CA 94612',
        phone: '800-464-4000',
        region: 'Regional',
        membership: 'Regional',
        insuredLives: '12.5 M',
        medicare: 'Yes',
        medicaid: 'Yes',
        commercial: 'Yes',
        logo: 'https://logo.clearbit.com/kp.org',
        type: 'Health System'
      },
      'Molina Healthcare': {
        website: 'www.molinahealthcare.com',
        address: '200 Oceangate, Long Beach, CA 90802',
        phone: '888-562-5442',
        region: 'National',
        membership: 'Regional',
        insuredLives: '5.2 M',
        medicare: 'Yes',
        medicaid: 'Yes',
        commercial: 'Yes',
        logo: 'https://logo.clearbit.com/molinahealthcare.com',
        type: 'Insurance'
      }
    };
    // Return specific payer details if available, otherwise generate mock data
    return payerMap[payerName] || {
      website: `www.${payerName.toLowerCase().replace(/\s+/g, '')}.com`,
      address: '123 Healthcare Ave, Insurance City, US 12345',
      phone: '800-555-1234',
      region: 'National',
      membership: 'Regional',
      insuredLives: `${(Math.random() * 30).toFixed(1)} M`,
      medicare: Math.random() > 0.3 ? 'Yes' : 'No',
      medicaid: Math.random() > 0.4 ? 'Yes' : 'No',
      commercial: 'Yes',
      logo: payer.payerLogo || 'https://uploadthingy.s3.us-west-1.amazonaws.com/8WzAsTXnYG6PbLoyjpzxJz/image.png',
      type: Math.random() > 0.7 ? 'Insurance' : 'Health System'
    };
  };
  const payerDetails = getPayerDetails(payer.payer);
  // Badge component for payer type
  const PayerTypeBadge = ({
    type
  }: {
    type: string;
  }) => {
    const bgColor = type === 'Insurance' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800';
    return <span className={`px-3 py-1 rounded-full text-xs font-medium ${bgColor}`} data-id="element-129">
        {type}
      </span>;
  };
  // Coverage indicator badge
  const CoverageBadge = ({
    covered,
    type
  }: {
    covered: boolean;
    type: string;
  }) => {
    const letter = type.charAt(0);
    const bgColor = covered ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200';
    return <div className={`w-8 h-8 rounded-full ${bgColor} border flex items-center justify-center font-medium text-sm`} data-id="element-130">
        {letter}
      </div>;
  };
  return <>
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black bg-opacity-30 z-20 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={onClose} data-id="element-131" />
      {/* Modal */}
      <div className={`fixed top-0 right-0 bottom-0 w-full md:w-5/6 lg:w-3/5 xl:w-1/2 bg-white shadow-xl z-30 transform transition-transform duration-300 ease-in-out overflow-auto ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-132">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 border-b" data-id="element-133">
          <div className="flex justify-between items-center p-4" data-id="element-134">
            <h2 className="text-xl font-semibold" data-id="element-135">Payer Details</h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-136">
              <XIcon size={20} className="text-gray-500" data-id="element-137" />
            </button>
          </div>
        </div>
        <div className="overflow-auto" data-id="element-138">
          {/* Header with gradient background */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6" data-id="element-139">
            <div className="flex items-center" data-id="element-140">
              <div className="mr-5" data-id="element-141">
                <div className="bg-white p-3 rounded-lg shadow-md border border-blue-200" data-id="element-142">
                  <img src={payerDetails.logo} alt={payer.payer} className="w-16 h-16 object-contain" onError={e => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = 'https://uploadthingy.s3.us-west-1.amazonaws.com/8WzAsTXnYG6PbLoyjpzxJz/image.png';
                }} data-id="element-143" />
                </div>
              </div>
              <div data-id="element-144">
                <div className="flex items-center space-x-3 mb-2" data-id="element-145">
                  <h3 className="text-2xl font-bold" data-id="element-146">{payer.payer}</h3>
                  <PayerTypeBadge type={payerDetails.type} data-id="element-147" />
                </div>
                <div className="flex items-center" data-id="element-148">
                  <GlobeIcon size={16} className="mr-2" data-id="element-149" />
                  <a href={`https://${payerDetails.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-100 hover:text-white hover:underline flex items-center" data-id="element-150">
                    {payerDetails.website}
                    <ExternalLinkIcon size={14} className="ml-1" data-id="element-151" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-8" data-id="element-152">
            {/* Contact Information */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-153">
              <div className="flex items-start mb-3" data-id="element-154">
                <MapPinIcon size={18} className="text-gray-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-155" />
                <div className="text-gray-700" data-id="element-156">{payerDetails.address}</div>
              </div>
              <div className="flex items-center" data-id="element-157">
                <PhoneIcon size={18} className="text-gray-500 mr-3 flex-shrink-0" data-id="element-158" />
                <div className="text-gray-700" data-id="element-159">{payerDetails.phone}</div>
              </div>
            </div>
            {/* Key Information Section */}
            <div data-id="element-160">
              <div className="flex items-center mb-4" data-id="element-161">
                <InfoIcon size={18} className="text-blue-600 mr-2" data-id="element-162" />
                <h4 className="text-lg font-medium text-gray-800" data-id="element-163">
                  Key Information
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-6" data-id="element-164">
                <div className="flex items-center" data-id="element-165">
                  <BuildingIcon size={18} className="text-indigo-500 mr-3 flex-shrink-0" data-id="element-166" />
                  <div data-id="element-167">
                    <div className="text-sm text-gray-500" data-id="element-168">Region</div>
                    <div className="font-medium" data-id="element-169">{payerDetails.region}</div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-170">
                  <GlobeIcon size={18} className="text-blue-500 mr-3 flex-shrink-0" data-id="element-171" />
                  <div data-id="element-172">
                    <div className="text-sm text-gray-500" data-id="element-173">Membership</div>
                    <div className="font-medium" data-id="element-174">{payerDetails.membership}</div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-175">
                  <UsersIcon size={18} className="text-emerald-500 mr-3 flex-shrink-0" data-id="element-176" />
                  <div data-id="element-177">
                    <div className="text-sm text-gray-500" data-id="element-178">Insured Lives</div>
                    <div className="font-medium" data-id="element-179">
                      {payerDetails.insuredLives}
                    </div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-180">
                  <HeartIcon size={18} className="text-red-500 mr-3 flex-shrink-0" data-id="element-181" />
                  <div data-id="element-182">
                    <div className="text-sm text-gray-500" data-id="element-183">Coverage Types</div>
                    <div className="flex space-x-2 mt-1" data-id="element-184">
                      <CoverageBadge covered={payerDetails.medicare === 'Yes'} type="Medicare" data-id="element-185" />
                      <CoverageBadge covered={payerDetails.medicaid === 'Yes'} type="Medicaid" data-id="element-186" />
                      <CoverageBadge covered={payerDetails.commercial === 'Yes'} type="Commercial" data-id="element-187" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Divider */}
            <div className="border-t border-gray-200" data-id="element-188"></div>
            {/* Coverage by State */}
            <div data-id="element-189">
              <div className="flex items-center mb-4" data-id="element-190">
                <ShieldIcon size={18} className="text-blue-600 mr-2" data-id="element-191" />
                <h4 className="text-lg font-medium text-gray-800" data-id="element-192">
                  Coverage by State
                </h4>
              </div>
              <div className="border rounded-md overflow-hidden" data-id="element-193">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-194">
                  <thead className="bg-gray-50" data-id="element-195">
                    <tr data-id="element-196">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-197">
                        State
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-198">
                        Commercial
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-199">
                        Medicare
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-200">
                        Medicaid
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-201">
                    {[{
                    state: 'New Mexico',
                    commercial: 'Yes',
                    medicare: 'Yes',
                    medicaid: 'Yes'
                  }, {
                    state: 'Arizona',
                    commercial: 'Yes',
                    medicare: 'Yes',
                    medicaid: 'No'
                  }, {
                    state: 'Colorado',
                    commercial: 'Yes',
                    medicare: 'Yes',
                    medicaid: 'Yes'
                  }, {
                    state: 'Texas',
                    commercial: 'Yes',
                    medicare: 'Yes',
                    medicaid: 'Yes'
                  }, {
                    state: 'Oklahoma',
                    commercial: 'Yes',
                    medicare: 'No',
                    medicaid: 'No'
                  }].map((state, idx) => <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-202">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-203">
                          {state.state}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-204">
                          {state.commercial}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-205">
                          {state.medicare}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-206">
                          {state.medicaid}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Divider */}
            <div className="border-t border-gray-200" data-id="element-207"></div>
            {/* Related Policies */}
            <div data-id="element-208">
              <div className="flex items-center mb-4" data-id="element-209">
                <ClipboardListIcon size={18} className="text-blue-600 mr-2" data-id="element-210" />
                <h4 className="text-lg font-medium text-gray-800" data-id="element-211">
                  Related Policies
                </h4>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-212">
                <ul className="space-y-3" data-id="element-213">
                  {[1, 2, 3].map(i => <li key={i} className="flex items-start" data-id="element-214">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 mr-3 flex-shrink-0" data-id="element-215"></div>
                      <div data-id="element-216">
                        <a href="#" className="text-blue-600 font-medium hover:underline flex items-center" data-id="element-217">
                          {payer.payer.includes('Blue Cross') ? `Radiofrequency Ablation Policy ${i}` : `${payer.payer} Medical Policy ${Math.floor(Math.random() * 1000)}`}
                          <ExternalLinkIcon size={14} className="ml-1" data-id="element-218" />
                        </a>
                        <div className="text-sm text-gray-500" data-id="element-219">
                          Last updated: {new Date().toLocaleDateString()}
                        </div>
                      </div>
                    </li>)}
                </ul>
              </div>
            </div>
            {/* Recent Updates */}
            <div data-id="element-220">
              <div className="flex items-center mb-4" data-id="element-221">
                <HistoryIcon size={18} className="text-blue-600 mr-2" data-id="element-222" />
                <h4 className="text-lg font-medium text-gray-800" data-id="element-223">
                  Recent Updates
                </h4>
              </div>
              <div className="space-y-4" data-id="element-224">
                <div className="border rounded-md overflow-hidden flex" data-id="element-225">
                  <div className="w-2 bg-blue-500" data-id="element-226"></div>
                  <div className="p-4 flex-1" data-id="element-227">
                    <div className="flex justify-between items-start mb-2" data-id="element-228">
                      <h5 className="font-medium" data-id="element-229">Policy Update</h5>
                      <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-230">
                        <CalendarIcon size={12} className="inline mr-1" data-id="element-231" />
                        {new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-232">
                      Updated coverage criteria for radiofrequency ablation
                      procedures.
                    </p>
                  </div>
                </div>
                <div className="border rounded-md overflow-hidden flex" data-id="element-233">
                  <div className="w-2 bg-green-500" data-id="element-234"></div>
                  <div className="p-4 flex-1" data-id="element-235">
                    <div className="flex justify-between items-start mb-2" data-id="element-236">
                      <h5 className="font-medium" data-id="element-237">New Coverage</h5>
                      <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-238">
                        <CalendarIcon size={12} className="inline mr-1" data-id="element-239" />
                        {new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-240">
                      Added coverage for new diagnostic procedures in Colorado
                      and New Mexico.
                    </p>
                  </div>
                </div>
                <div className="border rounded-md overflow-hidden flex" data-id="element-241">
                  <div className="w-2 bg-amber-500" data-id="element-242"></div>
                  <div className="p-4 flex-1" data-id="element-243">
                    <div className="flex justify-between items-start mb-2" data-id="element-244">
                      <h5 className="font-medium" data-id="element-245">Administrative Change</h5>
                      <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-246">
                        <CalendarIcon size={12} className="inline mr-1" data-id="element-247" />
                        {new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-248">
                      Updated prior authorization requirements for specialty
                      medications.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Notes */}
            <div className="border-t border-gray-200 pt-6" data-id="element-249">
              <div className="flex items-center mb-4" data-id="element-250">
                <BriefcaseIcon size={18} className="text-blue-600 mr-2" data-id="element-251" />
                <h4 className="text-lg font-medium text-gray-800" data-id="element-252">Notes</h4>
              </div>
              <div className="border rounded-md p-4 bg-gray-50" data-id="element-253">
                <p className="text-sm text-gray-600" data-id="element-254">
                  {payer.payer} is a {payerDetails.region.toLowerCase()}{' '}
                  healthcare insurance provider offering coverage for
                  {payerDetails.medicare === 'Yes' ? ' Medicare,' : ''}
                  {payerDetails.medicaid === 'Yes' ? ' Medicaid,' : ''}
                  {payerDetails.commercial === 'Yes' ? ' Commercial,' : ''}
                  and other health plans. Contact provider relations for more
                  information about specific coverage details.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}